// Sample Writing service config (Post a Project, Atrium Stage 7).
//
// Source of truth = the Rails backend, NOT the design prototype:
//   whitelist  app/controllers/api/v1/project_postings_controller.rb (sample_posting_params)
//              = genre niche subtitle tier title writing_style note fiction file
//   view       app/views/job_postings/steps/sample/_one.html.erb
//   validation app/models/job_postings/job_postings/step_validations.rb (sample__one)
//   cost       app/models/job_postings/sample_posting.rb (#cost — fixed 1,000 words)
//   parity     tasks/post-a-project/parity/writing-variants.md (SAMPLE)
//
// Restorations vs the design prototype:
//   - subtitle is optional
//   - requirements field key is `note` (NOT note_brief) and REQUIRED (validations:1077-1078)
//   - all-tier submitted value = "Allow All to Request" (not "All Tier Access")
//   - niche list = tier-scoped nichesForTier (niches_by_tier), matching the
//     sample__one server validation, so customers can't pick an out-of-tier niche
//   - word count is FIXED at 1,000 (no input; sample has no word_count attr)

import {
  FICTION_WRITING_TIERS,
  ALL_TIER_VALUE,
  WRITING_STYLES,
  WRITING_GENRES,
  RATES,
} from "../options"

// string[] -> [{ value, label }] for select / radio fields.
const asOptions = (arr) => arr.map((v) => ({ value: v, label: v }))

const money = (n) => Math.round(n * 100) / 100

// Per-100-word rate text for a tier card (display only; rail is authoritative).
// Server-traced via ctx.rates (ruling C2 / tuw-app:P-001), RATES seed as fallback.
// Per-100-word sample rate for one tier, resolved per-key: a null server leaf for
// that tier falls back to the RATES seed for THAT tier, never to 0 (tuw-app:P-001).
// Returns undefined for a tier absent from both maps (All-Tier / unknown) so the
// callers' `!= null` guards still short-circuit.
const sampleRateFor = (tier, ctx = {}) =>
  ctx.rates?.sample_writer_cost?.[tier] ?? RATES.SAMPLE_WRITER_COST[tier]

const rateText = (tier, ctx = {}) => {
  const rate = sampleRateFor(tier, ctx)
  return rate != null ? `$${rate.toFixed(2)} / 100 words` : undefined
}

// Concrete (non-All) tiers, used for the All-Tier "From" floor price.
const CONCRETE_TIERS = ["Premium", "Top", "Urban", "Rising"]

// All-Tier "From $X / 100 words" = the minimum concrete-tier rate (design tierOpts
// `From` idiom). Server-traced through sampleRateFor, RATES seed as fallback.
const allTierFromText = (ctx = {}) => {
  const rates = CONCRETE_TIERS.map((t) => sampleRateFor(t, ctx)).filter((r) => r != null)
  if (!rates.length) return undefined
  return `From $${Math.min(...rates).toFixed(2)} / 100 words`
}

// Design tier descriptions (service-config.js TIER_DESCS + All-tier copy). Em dash
// converted per the no-em-dash rule.
const TIER_DESCRIPTIONS = {
  [ALL_TIER_VALUE]:
    "Open the project to writers of every tier, you pay the rate of whoever you attach.",
  Premium: "Our highest-performing writers.",
  Top: "Proven, consistent writers.",
  Urban: "Up-and-coming writers with potential.",
  Rising: "Vetted writers just getting started.",
}

export const sample = {
  key: "sample",
  postingKey: "sample_posting",
  name: "Sample",

  steps: () => [
    // Sample is ONE real step (sample__one).
    {
      id: "brief",
      title: "Sample brief",
      sub: "Writers can request your sample project. Choose one writer for a paid 1,000-word sample, then decide whether to continue on a full book.",
      fields: [
        {
          type: "toggle",
          key: "fiction",
          label: "This sample is fiction",
        },
        {
          // All-Tier submits the backend value "Allow All to Request" (constants.rb
          // ALL_TIER_ACCESS), labelled "All Tier Access" per the design; that value
          // divergence is a registered options exception. Prices + tier descriptions
          // match the design (All-Tier shows the "From" floor).
          type: "radio",
          key: "tier",
          label: "Writer tier",
          required: true,
          options: (v = {}, ctx = {}) =>
            FICTION_WRITING_TIERS.map((tier) => ({
              value: tier,
              label: tier === ALL_TIER_VALUE ? "All Tier Access" : tier,
              price: tier === ALL_TIER_VALUE ? allTierFromText(ctx) : rateText(tier, ctx),
              sub: TIER_DESCRIPTIONS[tier],
            })),
        },
        // Fixed 1,000-word sample — read-only display, never serialized (sample
        // has no word_count attr; cost hardcodes SAMPLE_WORD_COUNT). The design's
        // `note` type renders a textarea in this build's FieldRenderer, so the
        // read-only fixed-words box uses the Phase-2 `wordcount`+`fixedLabel` idiom;
        // the resulting type divergence is a registered exception (no label, to
        // match the design note's label-less presentation).
        {
          type: "wordcount",
          fixedLabel: "Fixed at 1,000 words",
          help: "Sample length is fixed at 1,000 words, enough to judge a writer’s style, voice and quality before you commit.",
        },
        { type: "text", key: "title", label: "Title", required: true, placeholder: "Working title" },
        { type: "text", key: "subtitle", label: "Subtitle", required: false },
        {
          type: "select",
          key: "writing_style",
          label: "Writing style",
          required: true,
          blankLabel: "Select Writing Style",
          options: asOptions(WRITING_STYLES),
          when: (v) => !!v.fiction,
        },
        {
          type: "select",
          key: "genre",
          label: "Genre",
          required: true,
          blankLabel: "Select Genre",
          options: asOptions([...WRITING_GENRES].sort()),
          when: (v) => !!v.fiction,
        },
        {
          type: "select",
          key: "niche",
          label: "Niche / genre",
          required: true,
          blankLabel: "Select niche/genre",
          // Tier-scoped niches (niches_by_tier) — the server restricts sample
          // niches by tier (sample__one: Finance/Tech, Urban/Rising, else
          // WRITING_NICHES). Offering the full ALL_ORDER_NICHES let customers pick
          // an out-of-tier niche (e.g. "Business & Money") that the server then 422s.
          options: (v = {}, ctx = {}) =>
            asOptions((ctx.options && ctx.options.nichesForTier(v.tier)) || []),
          when: (v) => !v.fiction,
        },
        {
          type: "note",
          key: "note",
          label: "Requirements",
          required: true,
          rows: 4,
          placeholder: "Tell the writer what you want this sample to demonstrate.",
        },
        {
          type: "file",
          key: "file",
          label: "Requirements file",
          required: false,
          accept: ".doc,.docx,.pdf,.zip",
          placeholder: "Upload a file",
        },
      ],
    },
  ],

  // Instant estimate for the PriceRail. Ruby sample #cost = (1000 / 100.0) * rate
  // = 10 * SAMPLE_WRITER_COST[tier] (plus the shared add-on, contributed
  // separately). All-tier shows a "from" range (Rising..Premium).
  estimate: (values = {}, ctx = {}) => {
    const empty = { lines: [], total: 0, isRange: false, framing: "free-to-post" }

    const tier = values.tier
    if (!tier) return empty

    const allTier = tier === ALL_TIER_VALUE
    const rateTier = allTier ? "Rising" : tier
    const rate = sampleRateFor(rateTier, ctx)
    if (rate == null) return empty

    const amount = money((RATES.SAMPLE_WORD_COUNT / 100) * rate)

    return {
      lines: [
        {
          label: allTier ? "Sample (1,000 words, all tiers)" : `Sample (1,000 words, ${tier})`,
          amount,
        },
      ],
      total: amount,
      isRange: allTier,
      fromLabel: "Starting at",
      framing: "free-to-post",
    }
  },

  // /post/price entry (camelCase; underscored server-side). cost_fields for
  // sample_posting = [tier]. Null until a tier is chosen.
  pricePayload: (values = {}) => {
    if (!values.tier) return null

    return {
      postingType: "sample_posting",
      tier: values.tier,
    }
  },
}
